import {formatCurrency} from '@common/lib/utils';
import {Package, Users, Heart} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {RouterOutput, trpc} from '@common-web/lib/trpc';

type GroupPlanDetail =
  RouterOutput['vba']['groups']['groupPlans']['listGroupPlanDetails']['plans'][number];

interface GroupPlansTabProps {
  groupId?: string;
  agentId?: string;
  // Heading shown above the list (e.g. "Products" on the account view). Defaults
  // to "Plans"; the rest of the copy/actions are unchanged.
  title?: string;
}

// Read-only for now: editing plans/agents and navigating to plan/agent detail
// pages are intentionally not offered on the group view yet.
export const GroupPlansTab = ({groupId, agentId, title = 'Plans'}: GroupPlansTabProps) => {
  const {data, isLoading} = groupId
    ? trpc.vba.groups.groupPlans.listGroupPlanDetails.useQuery(
        {groupId},
        {refetchOnWindowFocus: false},
      )
    : agentId
      ? trpc.vba.groups.groupPlans.listGroupPlanDetailsByAgentId_REMOVE_LATER.useQuery(
          {agentId: agentId!},
          {refetchOnWindowFocus: false},
        )
      : {};

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <Skeleton className='h-8 w-48' />
          <Skeleton className='h-10 w-28' />
        </div>
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className='h-48 w-full rounded-xl' />
        ))}
      </div>
    );
  }

  const config = (plan: GroupPlanDetail) =>
    plan.groupPlanConfigurationVariation.groupPlanConfiguration;
  const variation = (plan: GroupPlanDetail) => plan.groupPlanConfigurationVariation;

  return (
    <div className='space-y-5'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{title}</h2>
          <p className='text-sm text-gray-500 mt-0.5'>
            {data?.plans?.length || 0} active plan{data?.plans?.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Plans List */}
      {data?.plans && data.plans.length > 0 ? (
        <div className='space-y-4'>
          {data.plans.map((plan) => {
            const supportsCommissions = config(plan).supportsCommissions;
            const features = variation(plan).vbaFeatureConfigurationPriceVariations;

            return (
              <div
                key={plan.id}
                className='bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm'
              >
                {/* Header */}
                <div className='p-4 border-b border-gray-100'>
                  <div className='flex items-start justify-between'>
                    <div>
                      <h3 className='font-semibold text-gray-900'>{config(plan).name}</h3>
                      <div className='flex items-center gap-2 mt-1'>
                        {variation(plan).name && (
                          <Badge variant='secondary' className='text-xs font-normal'>
                            {variation(plan).name}
                          </Badge>
                        )}
                        {config(plan).includesFamilyCoverage && (
                          <Badge className='bg-rose-50 text-rose-700 border-rose-200 text-xs'>
                            <Heart className='h-3 w-3 mr-1' />
                            Family
                          </Badge>
                        )}
                        <span className='text-sm font-medium text-gray-900'>
                          {formatCurrency(variation(plan).maxPayoutPEPM)}
                          <span className='font-normal text-gray-500'>/mo</span>
                          {(variation(plan).maxPayoutPEPMES ||
                            variation(plan).maxPayoutPEPMEC ||
                            variation(plan).maxPayoutPEPMFA) && (
                            <span className='ml-1 text-xs text-gray-400'>
                              {variation(plan).maxPayoutPEPMES &&
                                `ES:${formatCurrency(variation(plan).maxPayoutPEPMES!)}`}
                              {variation(plan).maxPayoutPEPMEC &&
                                ` EC:${formatCurrency(variation(plan).maxPayoutPEPMEC!)}`}
                              {variation(plan).maxPayoutPEPMFA &&
                                ` FA:${formatCurrency(variation(plan).maxPayoutPEPMFA!)}`}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className='p-4 space-y-4'>
                  {/* Features */}
                  {features.length > 0 && (
                    <div>
                      <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-2'>
                        Features
                      </div>
                      <div className='space-y-1'>
                        {features.map((pv) => {
                          const featureConfig = data.featureConfigurations.find(
                            (f) => f.id === pv.vbaFeatureConfigurationId,
                          );
                          return (
                            <div
                              key={pv.id}
                              className='flex items-center justify-between py-1.5 px-2 -mx-2'
                            >
                              <span className='flex items-center gap-2 text-sm text-gray-700'>
                                <Package className='h-4 w-4 text-gray-400' />
                                {featureConfig?.featureName || pv.priceName}
                              </span>
                              <span className='text-sm text-gray-500 font-medium'>
                                {formatCurrency(pv.employeeOnlyPEPM)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Commission Agents */}
                  {supportsCommissions && (
                    <div>
                      <div className='flex items-center justify-between mb-2'>
                        <div className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                          Agents
                        </div>
                      </div>
                      <div className='space-y-1'>
                        {config(plan).defaultCommissionAgent && plan.defaultAgentCommission && (
                          <div
                            key={'default'}
                            className='flex items-center justify-between py-1.5 px-2 -mx-2'
                          >
                            <span className='flex items-center gap-2 text-sm text-gray-700'>
                              <Users className='h-4 w-4 text-emerald-500' />
                              {config(plan).defaultCommissionAgent?.name}
                            </span>
                            <div className='flex items-center gap-2'>
                              <span className='text-sm text-emerald-600 font-medium'>
                                {formatCurrency(plan.defaultAgentCommission.EM)}
                              </span>
                              {config(plan).includesFamilyCoverage && (
                                <span className='text-xs text-gray-400'>
                                  ES:{formatCurrency(plan.defaultAgentCommission.ES)} EC:
                                  {formatCurrency(plan.defaultAgentCommission.EC)} FA:
                                  {formatCurrency(plan.defaultAgentCommission.FA)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {plan.commissionAgentPlans.length > 0 ? (
                        <div className='space-y-1'>
                          {plan.commissionAgentPlans.map((agentPlan) => {
                            const hasTierRates =
                              agentPlan.commissionRateES != null ||
                              agentPlan.commissionRateEC != null ||
                              agentPlan.commissionRateFA != null;
                            return (
                              <div
                                key={agentPlan.id}
                                className='flex items-center justify-between py-1.5 px-2 -mx-2'
                              >
                                <span className='flex items-center gap-2 text-sm text-gray-700'>
                                  <Users className='h-4 w-4 text-emerald-500' />
                                  {agentPlan.commissionAgent.name}
                                  {agentPlan.payrollCycleId && (
                                    <span className='text-xs text-gray-400'>
                                      ({agentPlan.payrollCycleId})
                                    </span>
                                  )}
                                </span>
                                <div className='flex items-center gap-2'>
                                  <span className='text-sm text-emerald-600 font-medium'>
                                    {formatCurrency(agentPlan.commissionRate)}
                                  </span>
                                  {hasTierRates && (
                                    <span className='text-xs text-gray-400'>
                                      {agentPlan.commissionRateES != null &&
                                        `ES:${formatCurrency(agentPlan.commissionRateES)}`}
                                      {agentPlan.commissionRateEC != null &&
                                        ` EC:${formatCurrency(agentPlan.commissionRateEC)}`}
                                      {agentPlan.commissionRateFA != null &&
                                        ` FA:${formatCurrency(agentPlan.commissionRateFA)}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className='text-sm text-gray-400 italic py-1'>No agents assigned</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className='bg-white border border-gray-200 border-dashed rounded-xl p-8 text-center'>
          <Package className='h-10 w-10 text-gray-300 mx-auto mb-3' />
          <div className='text-gray-500 mb-4'>No plans configured for this group</div>
        </div>
      )}
    </div>
  );
};
